import type { DirectorySearch } from "./search-params";
import { isOwnView, searchPagePath } from "./search-path";
import { SERVICE_TYPE_COPY } from "./service-type-copy";
import { localizePath } from "@/lib/locale";
import { pageMeta } from "@/lib/seo";
import { m } from "@/paraglide/messages.js";
import { baseLocale, getLocale, getUrlOrigin, locales } from "@/paraglide/runtime.js";
import type { Locale } from "@/paraglide/runtime.js";

/** What the page is called: the service and the place, as a person would say it. */
export const searchTitle = (search: DirectorySearch) => {
  const service = search.service ? SERVICE_TYPE_COPY[search.service].name() : null;
  if (service && search.online) return m.directory_title_online({ service });
  if (service && search.near) return m.directory_title_near({ service });
  if (service && search.city) return m.directory_title_service_city({ service, city: search.city });
  if (service) return service;
  if (search.q) return m.directory_title_query({ query: search.q });
  if (search.city) return m.directory_title_everyone_city({ city: search.city });
  return m.directory_title_all();
};

/**
 * The search page's head. A service, a city or both is a page of its own for search engines, the
 * way "terapia manualna Kraków" is on ZnanyLekarz, at an address of its own in each language
 * (ADR 0042). Free words, a position, a sort or an empty answer are the visitor's own view, kept
 * out of the index.
 */
export const searchHead = ({ search, total }: { search: DirectorySearch & { sort?: string }; total: number }) => {
  const title = searchTitle(search);
  const service = search.service ? SERVICE_TYPE_COPY[search.service].name() : null;
  const description =
    service && search.city
      ? m.directory_meta_description({ service, city: search.city })
      : service
        ? m.directory_meta_description_service({ service })
        : search.city
          ? m.directory_meta_description_city({ city: search.city })
          : m.directory_meta_description_all();
  const hidden = isOwnView(search) || total === 0;
  const urlIn = (locale: Locale) => `${getUrlOrigin()}${localizePath(searchPagePath(search), locale)}`;

  return {
    meta: [...pageMeta({ title, description }), ...(hidden ? [{ name: "robots", content: "noindex, follow" }] : [])],
    links: hidden
      ? []
      : [
          { rel: "canonical", href: urlIn(getLocale()) },
          ...locales.map((locale) => ({ rel: "alternate", hrefLang: locale, href: urlIn(locale) })),
          { rel: "alternate", hrefLang: "x-default", href: urlIn(baseLocale) },
        ],
  };
};
